// src/services/chatService.js
// チャット関連のFirestore操作

import {
  doc, setDoc, updateDoc, collection, addDoc,
  query, orderBy, limit, onSnapshot, serverTimestamp, increment
} from 'firebase/firestore';
import { db } from '../config/firebase';

// ── チャットルーム作成（ユーザー登録時に自動作成） ──
export async function createChatRoom(userId) {
  const roomRef = doc(db, 'chatRooms', userId);
  await setDoc(roomRef, {
    userId,
    lastMessage: '',
    lastMessageAt: serverTimestamp(),
    staffUnread: 0,
    userUnread: 0,
    phase: 'ai_chat', // ai_chat → staff_chat に切り替わる
  });
}

// ── メッセージ送信 ──
export async function sendMessage(roomId, { senderId, senderType, text, type = 'text', metadata = {} }) {
  // メッセージ追加
  const messagesRef = collection(db, 'chatRooms', roomId, 'messages');
  await addDoc(messagesRef, {
    senderId,
    senderType, // 'user' | 'staff' | 'ai'
    text,
    type, // 'text' | 'image' | 'job_card'
    metadata,
    createdAt: serverTimestamp(),
  });

  // チャットルームのlastMessage更新 + 未読数カウント
  const roomRef = doc(db, 'chatRooms', roomId);
  const unreadField = senderType === 'user' ? 'staffUnread' : 'userUnread';
  await updateDoc(roomRef, {
    lastMessage: type === 'text' ? text : (type === 'image' ? '📷 写真' : '💼 求人情報'),
    lastMessageAt: serverTimestamp(),
    [unreadField]: increment(1),
  });
}

// ── 求人カードをチャットで送信 ──
export async function sendJobCard(roomId, staffId, jobs, comment) {
  for (const job of jobs) {
    await sendMessage(roomId, {
      senderId: staffId,
      senderType: 'staff',
      text: comment || `${job.shopName}をご紹介します。`,
      type: 'job_card',
      metadata: {
        jobId: job.jobId,
        shopName: job.shopName,
        area: job.area,
        salary: job.salaryDisplay,
        photoUrl: job.photoUrl || '',
      },
    });
  }
}

// ── メッセージをリアルタイム購読 ──
export function subscribeMessages(roomId, callback) {
  const q = query(
    collection(db, 'chatRooms', roomId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(100)
  );
  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(messages.reverse());
  });
}

// ── チャットルーム一覧をリアルタイム購読（管理画面用） ──
export function subscribeChatRooms(callback) {
  const q = query(
    collection(db, 'chatRooms'),
    orderBy('lastMessageAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const rooms = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(rooms);
  });
}

// ── 未読リセット ──
export async function resetUnread(roomId, role) {
  const field = role === 'staff' ? 'staffUnread' : 'userUnread';
  await updateDoc(doc(db, 'chatRooms', roomId), { [field]: 0 });
}

// ── フェーズ切り替え（AI → スタッフ） ──
export async function switchToStaffChat(roomId) {
  await setDoc(doc(db, 'chatRooms', roomId), { phase: 'staff_chat', userId: roomId }, { merge: true });
}
